import { useEffect, useRef, useState, type MouseEvent, type SyntheticEvent } from "react";
import { useProposalFieldHistoryRequest } from "work/proposals/negotiation/proposal-field-history-request-context";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * Shared open/close state for a field-history trigger button — `ProposalFieldHistoryButton`'s icon
 * button and `ProposalFieldTimelineDropdown`'s icon button both open a popover/menu the same way.
 *
 * The trigger commonly sits inside a composite field control — a MUI X DatePicker's input adornment,
 * a text field's end adornment — whose ancestor has its own click handler (e.g. swapping a date field
 * into an editing state). An unguarded click bubbles into that ancestor on the same click that opened
 * the popover/menu, and the ancestor's handler can re-render or unmount the trigger's row, taking the
 * just-opened popover down with it before the user ever sees it (observed on the draft page's
 * "Response Due Date" field, whose date-picker adornment swaps branches on click). Stopping
 * propagation — and the default action, since these buttons render inside form controls — at the
 * trigger keeps the click from ever reaching that ancestor.
 *
 * Pass `field` to let the page open this trigger without a click: the change traverser stops at a
 * field carrying an unread change and asks that field's history to open itself (see
 * proposal-field-history-request-context.ts). Such a request has no click event to take an anchor
 * from, so the trigger reports its own element through `triggerRef` — a callback ref into state
 * rather than a ref object, because an anchor read from a ref during render is neither allowed nor
 * stable.
 *
 * Dismissal lives here too, because the popover/menu is a non-modal `Popper` (#4895): a `Modal` would
 * have closed on Escape and kept focus inside, and this surface deliberately does neither. So the
 * document hears Escape, and a focus that moves to anything outside both the trigger and the surface
 * closes it (#5572) — the reader who tabs on to the next field has left this one, and the panel must
 * not stay drawn over it. That is a *move*, not "focus is not inside": a stop the traverser opens
 * leaves focus wherever it already was, outside the panel, and the panel stays until the reader moves on.
 * The surface reports its element through `surfaceRef`, a callback ref for the same reason
 * `triggerRef` is one.
 */
/**
 * Keeps what happens inside the popover/menu from reaching the field control it was opened from.
 *
 * The surface renders in a portal, but React still bubbles a portal's events up the component tree,
 * into the same composite field control the trigger guards against. A date field takes any click or
 * focus inside it as "start editing" and swaps branches, so the first click inside the surface ("Show
 * full history", a value to restore) unmounted the surface it was made in (#6874). Spread these on the
 * surface's root element.
 *
 * Not key events: React stops a synthetic event's native one too, and Escape is heard on the document.
 */
const stopAtSurface = (event: SyntheticEvent) => event.stopPropagation();
export const proposalFieldHistorySurfaceEventGuards = {
  onClick: stopAtSurface,
  onFocus: stopAtSurface,
  onMouseDown: stopAtSurface,
} as const;

export function useProposalFieldHistoryPopoverTrigger<TElement extends HTMLElement = HTMLElement>(
  field?: ProposalField,
) {
  const [anchorEl, setAnchorEl] = useState<TElement | null>(null);
  const [triggerEl, setTriggerEl] = useState<TElement | null>(null);
  const [surfaceEl, setSurfaceEl] = useState<HTMLElement | null>(null);
  const { isRequested, onHonored } = useProposalFieldHistoryRequest(field);

  function open(event: MouseEvent<TElement>) {
    // MUI input adornments can keep the field itself focused when an adornment button is clicked.
    // Move focus to the trigger explicitly so a debounced field flushes its local value during this
    // opening click, before the user can choose a history entry on a later click. Otherwise the
    // still-pending field change can run alongside (or after) the revert and make the first revert
    // appear to do nothing; reopening the menu then works only because that pending edit has cleared.
    event.currentTarget.focus();
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }

  function close() {
    setAnchorEl(null);
    // Closing a page-requested history answers the request; leaving it standing would reopen the
    // popover the reader just dismissed.
    if (isRequested) onHonored?.();
  }

  // A request only opens the popover once the trigger has reported an element to anchor it to.
  const isOpen = anchorEl !== null || (isRequested && triggerEl !== null);

  // `close` is read through a ref because the page rebuilds the history request it closes over on every
  // render: naming it as a dependency would tear the listeners down and rebuild them just as often, while
  // `isOpen` is what actually decides whether anything should be listening.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") closeRef.current();
    }
    function handleFocusIn(event: FocusEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerEl?.contains(target) || surfaceEl?.contains(target)) return;
      closeRef.current();
    }
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [isOpen, triggerEl, surfaceEl]);

  return {
    anchorEl: anchorEl ?? triggerEl,
    isOpen,
    open,
    close,
    triggerRef: setTriggerEl,
    surfaceRef: setSurfaceEl,
  };
}
